/**
 * The employee model. This is the product's central object, not a wrapper over
 * tasks - see docs/adr/0018-employee-first-domain-model.md.
 *
 * Type and status are deliberately separate concepts: a Generalist can be on
 * probation, a Specialist can be retired.
 */
/**
 * `as const` arrays and not bare unions, because `packages/db` decodes a row
 * with `oneOf(row, 'employee_type', EMPLOYEE_TYPES)` and needs the values at
 * runtime - the same shape `MEMBERSHIP_ROLES` already takes in `http/workspace.ts`.
 * A CHECK constraint in `0002_employee.up.sql` carries the same list; the
 * repository spec asserts a value outside it is rejected rather than decoded.
 */
export const EMPLOYEE_TYPES = ['GENERALIST', 'SPECIALIST'] as const;
export type EmployeeType = (typeof EMPLOYEE_TYPES)[number];

export const EMPLOYEE_STATUSES = ['DRAFT', 'PROBATION', 'ACTIVE', 'PAUSED', 'RETIRED'] as const;
export type EmployeeStatus = (typeof EMPLOYEE_STATUSES)[number];

export interface Employee {
  readonly id: string;
  readonly workspaceId: string;
  readonly name: string;
  readonly employeeType: EmployeeType;
  readonly status: EmployeeStatus;
  readonly roleTitle: string;
  readonly jobDescription: string;
  /**
   * Optional, because `employee_policy` does not exist yet. EMP-001 does not
   * invent that table, and a required reference to one nobody has written is a
   * field every writer would have to make up - the same reasoning
   * `0001_tenancy.up.sql` gives for `role` being a check constraint rather than
   * a `role_id`. EMP-011 makes it real.
   */
  readonly policyId?: string;
  readonly createdAt: string;
}

export type DutyTriggerType = 'MANUAL' | 'SCHEDULE';

/**
 * A standing responsibility. The user-facing object; a scheduled task is its
 * runtime primitive underneath, never the other way round.
 */
export interface EmployeeDuty {
  readonly id: string;
  readonly workspaceId: string;
  readonly employeeId: string;
  readonly status: 'ACTIVE' | 'PAUSED';
  readonly instruction: string;
  readonly triggerType: DutyTriggerType;
  readonly scheduleRef?: string;
  readonly version: number;
}

export interface EmployeeKpi {
  readonly id: string;
  readonly workspaceId: string;
  readonly employeeId: string;
  readonly name: string;
  readonly unit: string;
  readonly target?: number;
  readonly period: 'DAY' | 'WEEK' | 'MONTH';
}
